/**
 * ESRS S1 — Own Workforce
 *
 * Workforce characteristics and social metrics: S1-8 collective bargaining
 * coverage, S1-13 training & skills development, S1-16 gender pay gap.
 * Records are append-only per entity + reporting period; the disclosure
 * reads the LATEST record (versions = restatement trail). Gender pay gap
 * = (male − female) / male × 100, which also feeds SFDR PAI 12.
 *
 * Backend contract (backend/routes/esrs_s1_routes.py):
 *   GET  /api/esrs/s1/disclosure         → { headcount: {...}, esrs_s1_8: {...},
 *                                            esrs_s1_13: {...}, esrs_s1_16: {...}, versions }
 *   GET  /api/esrs/s1/records            → { records: [...] }
 *   POST /api/esrs/s1/workforce-records  → record
 *     Server rules: female+male+other ≤ headcount_total;
 *     employees_trained_count ≤ headcount_total; both hourly pays > 0.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Users, Scale, Plus, X, History, TrendingUp, TrendingDown, Equal, Gauge,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C, SANS,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

// Pay gap → Badge tone + trend icon (S1-16 / SFDR PAI 12 semantics: positive
// gap means men earn more; > 5 % is the flag threshold used across the app).
function gapBadge(gap) {
  if (gap === null || gap === undefined || isNaN(Number(gap))) {
    return <Badge c={C.neutral} Icon={Equal} label="No data" />;
  }
  const g = Number(gap);
  if (g > 5) return <Badge c={C.destructive} Icon={TrendingUp} label={`${fmt(g, 2)}% gap`} />;
  if (g > 0) return <Badge c={C.accent} Icon={TrendingUp} label={`${fmt(g, 2)}% gap`} />;
  if (g < 0) return <Badge c={C.locked} Icon={TrendingDown} label={`${fmt(g, 2)}% gap`} />;
  return <Badge c={C.primary} Icon={Equal} label="Parity" />;
}

const gapColor = (gap) => {
  const g = Number(gap);
  if (isNaN(g)) return C.ink;
  if (g > 5) return C.destructive;
  if (g > 0) return C.accent;
  return C.primary;
};

const num = (v, d = 2) => <Data color={C.body}>{fmt(v, d)}</Data>;

const EMPTY_FORM = {
  headcount_total: "",
  headcount_female: "",
  headcount_male: "",
  headcount_other: "",
  training_hours_total: "",
  employees_trained_count: "",
  cb_coverage_pct: "",
  avg_gross_hourly_pay_female: "",
  avg_gross_hourly_pay_male: "",
};

export default function S1Workforce() {
  const { entities, entityId, setEntityId } = useEntities("esrs_s1_entity_id");
  const [period, setPeriod] = useState("FY2026");
  const [periodDraft, setPeriodDraft] = useState("FY2026");

  const [disclosure, setDisclosure] = useState(null);
  const [records, setRecords] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const q = `entity_id=${encodeURIComponent(entityId)}&reporting_period=${encodeURIComponent(period)}`;
    try {
      const [disc, recs] = await Promise.all([
        apiFetch(`/api/esrs/s1/disclosure?${q}`),
        apiFetch(`/api/esrs/s1/records?${q}`),
      ]);
      setDisclosure(disc || null);
      setRecords(recs?.records || []);
    } catch (e) {
      setError(e.message || "Failed to load workforce data");
      setDisclosure(null);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Client-side mirrors of the server validation rules.
  const totalN = Number(form.headcount_total || 0);
  const splitSum =
    Number(form.headcount_female || 0) +
    Number(form.headcount_male || 0) +
    Number(form.headcount_other || 0);
  const splitExceeds = form.headcount_total !== "" && splitSum > totalN;
  const trainedExceeds =
    form.headcount_total !== "" &&
    form.employees_trained_count !== "" &&
    Number(form.employees_trained_count) > totalN;
  const paysValid =
    Number(form.avg_gross_hourly_pay_female) > 0 &&
    Number(form.avg_gross_hourly_pay_male) > 0;
  const canSubmit =
    form.headcount_total !== "" && paysValid && !splitExceeds && !trainedExceeds;

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId || !canSubmit) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/esrs/s1/workforce-records", {
        method: "POST",
        body: {
          entity_id: entityId,
          reporting_period: period,
          headcount_total: Number(form.headcount_total || 0),
          headcount_female: Number(form.headcount_female || 0),
          headcount_male: Number(form.headcount_male || 0),
          headcount_other: Number(form.headcount_other || 0),
          training_hours_total: Number(form.training_hours_total || 0),
          employees_trained_count: Number(form.employees_trained_count || 0),
          cb_coverage_pct: Number(form.cb_coverage_pct || 0),
          avg_gross_hourly_pay_female: Number(form.avg_gross_hourly_pay_female),
          avg_gross_hourly_pay_male: Number(form.avg_gross_hourly_pay_male),
        },
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to record workforce data");
    } finally {
      setSaving(false);
    }
  };

  const periodInput = (
    <input
      value={periodDraft}
      onChange={(e) => setPeriodDraft(e.target.value)}
      onBlur={() => setPeriod(periodDraft)}
      onKeyDown={(e) => { if (e.key === "Enter") setPeriod(periodDraft); }}
      aria-label="Reporting period"
      placeholder="FY2026"
      style={{ ...field, width: 110, textAlign: "center" }}
    />
  );

  const header = (
    <PageHeader
      overline="ESRS S1 · Own Workforce"
      OverlineIcon={Users}
      title="Own Workforce"
      subtitle="Workforce characteristics and social metrics — S1-8 collective bargaining coverage, S1-13 training and skills development, S1-16 gender pay gap. The S1-16 gap feeds SFDR PAI 12 directly."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
      actions={periodInput}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={Users}
            title="No reporting entity selected"
            body="Choose a reporting entity to view its ESRS S1 own-workforce disclosure, restatement trail and pay-gap metrics."
          />
        </Card>
      </Page>
    );
  }

  const d = disclosure || {};
  const hc = d.headcount || {};
  const s18 = d.esrs_s1_8 || {};
  const s113 = d.esrs_s1_13 || {};
  const s116 = d.esrs_s1_16 || {};

  const kpis = [
    { label: "Total headcount", value: fmt(hc.total, 0) },
    { label: "Gender pay gap", value: fmt(s116.gender_pay_gap_pct, 2), unit: "%", color: gapColor(s116.gender_pay_gap_pct) },
    { label: "CB coverage (S1-8)", value: fmt(s18.cb_coverage_pct, 1), unit: "%", color: C.locked },
    { label: "Avg training h / employee", value: fmt(s113.avg_training_hours_per_employee, 1), unit: "h" },
    { label: "Employees trained", value: fmt(s113.employees_trained_pct, 1), unit: "%", color: C.primary },
  ];

  const recordColumns = [
    {
      key: "reporting_period",
      label: "Period",
      render: (r) => <Data>{r.reporting_period || "—"}</Data>,
    },
    {
      key: "headcount",
      label: "Headcount (F/M/O)",
      align: "right",
      render: (r) => (
        <span>
          <Data color={C.ink}>{fmt(r.headcount_total, 0)}</Data>{" "}
          <Data color={C.muted} weight={400}>
            {fmt(r.headcount_female, 0)}/{fmt(r.headcount_male, 0)}/{fmt(r.headcount_other, 0)}
          </Data>
        </span>
      ),
    },
    { key: "cb_coverage_pct", label: "CB %", align: "right", render: (r) => num(r.cb_coverage_pct, 1) },
    { key: "training_hours_total", label: "Training h", align: "right", render: (r) => num(r.training_hours_total, 1) },
    { key: "employees_trained_count", label: "Trained", align: "right", render: (r) => num(r.employees_trained_count, 0) },
    {
      key: "pay",
      label: "Pay F / M (€/h)",
      align: "right",
      render: (r) => (
        <Data color={C.body}>
          {fmt(r.avg_gross_hourly_pay_female)} / {fmt(r.avg_gross_hourly_pay_male)}
        </Data>
      ),
    },
    {
      key: "gender_pay_gap_pct",
      label: "Pay gap",
      render: (r) => gapBadge(r.gender_pay_gap_pct),
    },
  ];

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !disclosure ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={110} />
          <Skeleton h={160} />
          <Skeleton h={200} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* KPI strip */}
          <Card>
            <Overline Icon={Gauge}>S1 disclosure · {period}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              {kpis.map((k) => (
                <Metric key={k.label} label={k.label} value={k.value} unit={k.unit} color={k.color} />
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                alignItems: "center",
                fontFamily: SANS,
                fontSize: 12,
                color: C.body,
              }}
            >
              <span>
                Headcount F/M/O{" "}
                <Data color={C.ink}>
                  {fmt(hc.female, 0)}/{fmt(hc.male, 0)}/{fmt(hc.other, 0)}
                </Data>
              </span>
              <span>
                Hourly pay F vs M{" "}
                <Data color={C.ink}>
                  {fmt(s116.avg_gross_hourly_pay_female)} / {fmt(s116.avg_gross_hourly_pay_male)}
                </Data>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <History size={12} strokeWidth={1.5} />
                <Data color={C.muted}>v{fmt(d.versions, 0)}</Data> · latest record wins
              </span>
            </div>
          </Card>

          {/* S1-16 pay gap detail */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <Overline Icon={Scale} color={C.body} style={{ marginBottom: 0 }}>
                S1-16 gender pay gap
              </Overline>
              {gapBadge(s116.gender_pay_gap_pct)}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              <Metric label="Avg gross hourly pay — female" value={fmt(s116.avg_gross_hourly_pay_female)} unit="€/h" />
              <Metric label="Avg gross hourly pay — male" value={fmt(s116.avg_gross_hourly_pay_male)} unit="€/h" />
              <Metric
                label="Pay gap"
                value={fmt(s116.gender_pay_gap_pct, 2)}
                unit="%"
                color={gapColor(s116.gender_pay_gap_pct)}
              />
            </div>
            <p style={{ fontFamily: SANS, fontSize: 11, color: C.muted, margin: "12px 0 0" }}>
              Gap = (male − female) ÷ male × 100, on average gross hourly pay (ESRS S1-16; reported as SFDR PAI 12).
            </p>
          </Card>

          {/* Restatement trail */}
          <Card>
            <Overline Icon={History} color={C.body}>
              Workforce records · restatement trail
            </Overline>
            {records.length === 0 ? (
              <EmptyState
                Icon={Users}
                title="No workforce records yet"
                body="Record headcount, collective bargaining coverage, training and pay data to build the ESRS S1 disclosure for this period. Records are append-only — the disclosure always reads the latest one."
              />
            ) : (
              <DataTable
                columns={recordColumns}
                rows={records}
                keyField="record_id"
                emptyLabel="No workforce records"
              />
            )}
          </Card>

          {/* Record workforce data */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Overline Icon={Plus} color={C.body} style={{ marginBottom: 0 }}>
                Record workforce data
              </Overline>
              <Btn
                variant={showForm ? "ghost" : "secondary"}
                Icon={showForm ? X : Plus}
                onClick={() => setShowForm((s) => !s)}
              >
                {showForm ? "Cancel" : "Add record"}
              </Btn>
            </div>

            {showForm && (
              <form onSubmit={submit} style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <Label>Headcount total *</Label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.headcount_total}
                      onChange={(e) => setField("headcount_total", e.target.value)}
                      required
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Headcount — female</Label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.headcount_female}
                      onChange={(e) => setField("headcount_female", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Headcount — male</Label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.headcount_male}
                      onChange={(e) => setField("headcount_male", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Headcount — other</Label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.headcount_other}
                      onChange={(e) => setField("headcount_other", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Training hours — total (S1-13)</Label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={form.training_hours_total}
                      onChange={(e) => setField("training_hours_total", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Employees trained (count)</Label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.employees_trained_count}
                      onChange={(e) => setField("employees_trained_count", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Collective bargaining coverage % (S1-8)</Label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      value={form.cb_coverage_pct}
                      onChange={(e) => setField("cb_coverage_pct", e.target.value)}
                      placeholder="0"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Avg gross hourly pay — female (€/h) *</Label>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      value={form.avg_gross_hourly_pay_female}
                      onChange={(e) => setField("avg_gross_hourly_pay_female", e.target.value)}
                      required
                      placeholder="0.00"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Avg gross hourly pay — male (€/h) *</Label>
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      value={form.avg_gross_hourly_pay_male}
                      onChange={(e) => setField("avg_gross_hourly_pay_male", e.target.value)}
                      required
                      placeholder="0.00"
                      style={field}
                    />
                  </div>
                </div>
                {splitExceeds && (
                  <p style={{ fontFamily: SANS, fontSize: 11, color: C.destructive, margin: "10px 0 0" }}>
                    Female + male + other ({fmt(splitSum, 0)}) exceeds headcount total ({fmt(totalN, 0)}) — the server will reject this record.
                  </p>
                )}
                {trainedExceeds && (
                  <p style={{ fontFamily: SANS, fontSize: 11, color: C.destructive, margin: "10px 0 0" }}>
                    Employees trained ({fmt(form.employees_trained_count, 0)}) exceeds headcount total ({fmt(totalN, 0)}) — the server will reject this record.
                  </p>
                )}
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <Btn type="submit" Icon={Plus} disabled={saving || !canSubmit}>
                    {saving ? "Saving…" : "Record"}
                  </Btn>
                  <Btn variant="secondary" onClick={() => { setForm(EMPTY_FORM); setShowForm(false); }}>
                    Reset
                  </Btn>
                </div>
                <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, margin: "10px 0 0" }}>
                  Records are append-only for {period}: submitting adds a new version to the restatement trail and the disclosure reads the latest one. The pay gap is computed by the platform as (male − female) ÷ male × 100.
                </p>
              </form>
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
